/**
 * Das **Namensband** einer Befundzelle: wer an dieser Stelle etwas gesehen hat.
 *
 * Die Agenten und Menschen kommagetrennt und dezent, daneben das Zeichen
 * „Regel", sobald ein Kontrollmuster beteiligt war.
 *
 * **Ein Zeichen und kein Name in der Reihe.** Ein Muster ist keine Meinung,
 * sondern eine Regel, die immer anschlägt (ADR-0010); seine Beschriftung
 * zwischen Agentennamen läse sich wie ein weiterer Agent und zöge den Eindruck
 * von Übereinstimmung künstlich hoch. Das Zeichen steht einmal da, gleich wie
 * viele Muster angeschlagen haben — die Auskunft lautet „hier war auch eine
 * Regel beteiligt" und nicht „welche". Welche es waren, steht im Titel.
 *
 * An einer Stelle für beide Listen: Der Revisionsstrom und der Befundvorrat
 * nennen dieselben Urheber, und zwei Schreibweisen wären zwei Wahrheiten über
 * denselben Fund.
 */

import type { ReactElement } from "react";

import { Chip } from "../design-system";
import type { SiteAuthor } from "../api/client";
import { RULE, carriesRule, labelOfAuthors, labelOfRules } from "./findings";

export function AuthorBand({
  authors,
  width,
}: {
  authors: SiteAuthor[];
  /** Woran die Namen abgeschnitten werden — die Breite ihrer Spalte. */
  width: number;
}): ReactElement | null {
  if (authors.length === 0) {
    return null;
  }

  const names = labelOfAuthors(authors);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--sp-3)",
        maxWidth: width,
        minWidth: 0,
      }}
    >
      {names === "" ? null : (
        <span
          title={names}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "var(--fs-11)",
            color: "var(--fg-6)",
          }}
        >
          {names}
        </span>
      )}
      {carriesRule(authors) ? (
        <span title={labelOfRules(authors)}>
          <Chip>{RULE}</Chip>
        </span>
      ) : null}
    </span>
  );
}
